import { QueryClient } from '@tanstack/react-query';
import { setupOnlineManager } from './onlineManagerSetup';
import {
  NO_RETRY,
  retryDelay,
  shouldRetryQuery as shouldRetryQueryBase,
} from './retryPolicy';
import logger from '../Utils/logger';

// Browser online/offline → React Query onlineManager
setupOnlineManager();

const shouldRetryQuery = (failureCount, error) => {
  const should = shouldRetryQueryBase(failureCount, error);
  if (should) {
    logger.debug('React Query query retry', {
      attempt: failureCount + 1,
      message: error?.message,
      status: error?.response?.status,
    });
  }
  return should;
};

/**
 * React Query Client Configuration
 *
 * - networkMode: 'online' — pause while offline; resume when back online
 * - Queries: limited retries + exponential backoff on network/5xx/429
 * - Mutations: default no retry (booking/payment safety); safe hooks opt in
 * - refetchOnReconnect: true for queries so stale GETs refresh after reconnect
 */

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      gcTime: 5 * 60 * 1000,
      networkMode: 'online',
      retry: shouldRetryQuery,
      retryDelay,
      refetchOnWindowFocus: false,
      // Refresh stale GET data when connectivity returns (mutations stay paused/resumed via networkMode)
      refetchOnReconnect: true,
      refetchOnMount: false,
      // A failed background refresh should not replace usable page data.
      throwOnError: (error, query) => query.state.data === undefined &&
        error?.response?.status >= 500 && error?.response?.status < 600,
    },
    mutations: {
      networkMode: 'online',
      // Default: do not auto-retry writes (booking create / payment / cancel / reschedule)
      retry: NO_RETRY,
      retryDelay,
      onError: (error, _variables, _context) => {
        logger.error('Mutation failed (final)', {
          message: error?.message,
          status: error?.response?.status,
          code: error?.code,
        });
      },
    },
  },
});

export default queryClient;
